import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, MessageCircle } from "lucide-react";
import { BRAND, IMAGES } from "../lib/brand";
import { MaskLine, Reveal } from "../components/motion";
import { px } from "../lib/api";
import InstaFeed from "../components/InstaFeed";

const MANIFESTO = [
  { n: "01", title: "12-Hour Marination", text: "Every bird bathes in hung curd, raw papaya and freshly ground masala overnight. Flavour to the bone — never surface deep." },
  { n: "02", title: "Coal, Not Corners", text: "Real tandoor. Real smoke. We don't cut corners to cut costs. What leaves our kitchen is what we'd feed our own family." },
  { n: "03", title: "Hot At Your Door", text: "Insulated, leak-proof packaging engineered so your kukkad arrives crunchy and steaming — high-rise or corner office." },
];

const CATEGORIES = [
  { label: "Kebabs", img: IMAGES.heroKebab, span: "lg:col-span-2 lg:row-span-2", to: "/menu" },
  { label: "Butter Chicken", img: IMAGES.spread, span: "", to: "/menu" },
  { label: "Mutton", img: IMAGES.grillMoody, span: "", to: "/menu" },
  { label: "Veg Delights", img: IMAGES.dal, span: "lg:col-span-2", to: "/menu" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yMascot = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden pt-24 pb-16">
        <motion.div style={{ y: yImg }} className="absolute inset-0 z-0" aria-hidden="true">
          <img src={px(IMAGES.grillMoody, 1600)} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-brown/60 via-brown/80 to-brown" />
        </motion.div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 w-full">
          <div className="grid lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                data-testid="hero-parent-badge"
                className="inline-flex items-center gap-2 border border-orange/50 rounded-full px-3.5 py-1.5 mb-4"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-orange" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-cream/80 font-bold">
                  A <span className="text-orange">{BRAND.parent}</span> brand
                </span>
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="font-accent text-chick text-3xl sm:text-4xl mb-2"
              >
                Freshly launched in Noida
              </motion.p>
              <h1 className="font-heading uppercase text-cream tracking-tighter leading-[0.82] text-6xl sm:text-8xl lg:text-[9.5rem]">
                <MaskLine delay={0.25}>Seriously</MaskLine>
                <MaskLine delay={0.38} className="text-orange">Good</MaskLine>
                <MaskLine delay={0.51}>Kukkad.</MaskLine>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="text-cream/80 text-lg sm:text-xl max-w-xl mt-6"
              >
                Chicken, kebabs & mutton done the desi way — bold, fresh and built for a generation that eats with its eyes first.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.95 }}
                className="flex flex-wrap gap-3 mt-8"
              >
                <Link
                  to="/menu"
                  data-testid="hero-order-cta"
                  className="group flex items-center gap-2 bg-orange text-primary-foreground font-bold uppercase px-7 py-4 rounded-md hover:scale-105 transition-transform shadow-hard-cream"
                >
                  Order Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href={BRAND.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-whatsapp-cta"
                  className="flex items-center gap-2 border border-cream/40 text-cream font-bold uppercase px-7 py-4 rounded-md hover:bg-cream hover:text-brown transition-colors"
                >
                  <MessageCircle className="w-5 h-5" /> WhatsApp
                </a>
              </motion.div>
            </div>

            <motion.div style={{ y: yMascot }} className="lg:col-span-4 hidden lg:flex justify-end">
              <motion.img
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                src={BRAND.mascot}
                alt={BRAND.name}
                className="w-full max-w-sm object-contain rounded-3xl drop-shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-orange text-primary-foreground py-4 border-y-2 border-brown overflow-hidden" aria-hidden="true">
        <Marquee speed={60} gradient={false}>
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="font-heading uppercase text-3xl sm:text-4xl mx-6 flex items-center gap-6">
              Grilled · Fried · Curried · Loaded
              <span className="text-brown">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <p className="font-accent text-chick text-2xl">Why we’re different</p>
          <h2 className="font-heading uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none mt-2 max-w-3xl">
            No shortcuts.<br /><span className="text-stroke-orange">No compromise.</span>
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8 mt-16">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.1} className="border-t-2 border-orange pt-6">
              <span className="font-heading text-orange text-5xl">{m.n}</span>
              <h3 className="font-heading uppercase text-2xl mt-3 tracking-tight">{m.title}</h3>
              <p className="text-cream/70 mt-3">{m.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BENTO CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-24 sm:pb-32">
        <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <h2 className="font-heading uppercase text-5xl sm:text-6xl tracking-tighter leading-none">
            Eat the <span className="text-orange">favourites</span>
          </h2>
          <Link to="/menu" data-testid="home-full-menu-link" className="flex items-center gap-2 text-cream font-bold uppercase text-sm hover:text-orange transition-colors">
            Full Menu <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[200px] gap-4">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.label} delay={i * 0.08} className={c.span}>
              <Link
                to={c.to}
                data-testid={`bento-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                className="group relative block w-full h-full rounded-lg overflow-hidden border border-border"
              >
                <img src={px(c.img, 900)} alt={c.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-brown-deep via-brown-deep/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-5">
                  <h3 className="font-heading uppercase text-2xl sm:text-3xl tracking-tight">{c.label}</h3>
                  <span className="text-orange text-sm font-bold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Order <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PARALLAX STATEMENT */}
      <ParallaxBand />

      {/* PACKAGING SHOWCASE */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="font-accent text-chick text-2xl">Branding you’ll wanna ’gram</p>
          <h2 className="font-heading uppercase text-5xl sm:text-6xl tracking-tighter leading-none mt-2">
            Packed with <span className="text-orange">personality</span>
          </h2>
          <p className="text-cream/70 mt-5 text-lg">
            From our cheeky chick mascot to leak-proof boxes that keep the crunch alive — every order lands like a little gift. Tick the box: Grilled, Fried, Curried, Loaded.
          </p>
          <Link
            to="/about"
            data-testid="home-about-link"
            className="inline-flex items-center gap-2 mt-7 text-cream font-bold uppercase text-sm hover:text-orange transition-colors"
          >
            Our Story <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-lg overflow-hidden border border-border shadow-hard">
            <img src={BRAND.packaging} alt="The Kukkad Co. packaging" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </section>

      {/* INSTAGRAM LIVE REELS */}
      <InstaFeed />
    </div>
  );
}

function ParallaxBand() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);
  return (
    <section ref={ref} className="relative h-[60vh] overflow-hidden flex items-center justify-center">
      <motion.div style={{ y }} className="absolute inset-0 z-0" aria-hidden="true">
        <img src={px(IMAGES.spread, 1600)} alt="" className="w-full h-[130%] object-cover" />
        <div className="absolute inset-0 bg-brown/80" />
      </motion.div>
      <Reveal className="relative z-10 text-center px-5">
        <p className="font-accent text-chick text-2xl">Serving all of Noida</p>
        <h2 className="font-heading uppercase text-cream text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.85]">
          Dine in.<br />Or we’ll <span className="text-orange">deliver</span>.
        </h2>
      </Reveal>
    </section>
  );
}
